import React from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import RightSheet from "../../../components/ui-components/RightSheet";
import BudgetBurndownChart from "../../cost-control/BudgetBurndownChart";
import { getBudgetSeries } from "../../cost-control/budgetSeriesApi";
import { effectivePolicy, policyHasSignoffGate } from "../overviewDerive";

const fmtEur = (n) =>
  new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const TRIGGER_LABEL = { soft: "Soft cap", hard: "Hard cap", predictive: "Predictive", spike: "Spike" };

// stepPctLabel prefixes a soft/hard rung with the policy's threshold percent.
function stepPctLabel(policy, trigger) {
  if (trigger === "soft" && policy.soft_pct != null) return `${policy.soft_pct}% · `;
  if (trigger === "hard" && policy.hard_pct != null) return `${policy.hard_pct}% · `;
  return "";
}

function Figure({ label, value, danger }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-text-3">{label}</span>
      <span className={`tabular-nums text-sm font-semibold ${danger ? "text-danger" : "text-text"}`}>{value}</span>
    </div>
  );
}
Figure.propTypes = { label: PropTypes.string, value: PropTypes.string, danger: PropTypes.bool };

function Section({ title, children }) {
  return (
    <div className="mt-4 border-t border-card-bd pt-4">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-2">{title}</div>
      {children}
    </div>
  );
}
Section.propTypes = { title: PropTypes.string, children: PropTypes.node };

// EnforcementBudgetDrawer — the per-budget drill-in. It REUSES Financial Control's burn-down
// chart + series endpoint (so the spend/forecast/cap picture is identical to Control) and adds
// the two enforcement-specific sections Control has no concept of: the guardrail ladder and the
// sign-off actions. The product can't block spend, so "prevention" here is the sign-off gate.
export default function EnforcementBudgetDrawer({ selected, policies = [], period, approvalRequestId = null, onClose, onAction }) {
  const open = !!selected;
  const budgetId = selected?.budget_id;

  const { data, isError } = useQuery({
    queryKey: ["budget-series", period, budgetId],
    queryFn: () => getBudgetSeries({ period, budgetId }),
    enabled: open && budgetId != null,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const series = data?.series || [];
  const cap = data?.cap ?? selected?.budget ?? null;
  const policy = selected ? effectivePolicy(selected, policies) : null;
  const hasGate = policyHasSignoffGate(policy);
  const handle = (kind) => onAction && onAction(kind, selected);

  const footer = selected ? (
    <button
      type="button"
      onClick={() => handle("open_budget")}
      className="block w-full rounded-ctrl bg-primary px-4 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-primary-hover"
    >
      Open budget →
    </button>
  ) : null;

  return (
    <RightSheet open={open} onClose={onClose} title={selected?.name} subtitle={period} footer={footer}>
      {!selected ? null : (
        <>
          {/* Reused Control burn-down chart. Degrades to a quiet note if no series is available. */}
          {isError || series.length === 0 ? (
            <div className="rounded-card border border-card-bd bg-surface-2 py-6 text-center text-xs text-text-3">
              Burn-down chart unavailable for this budget.
            </div>
          ) : (
            <BudgetBurndownChart
              cap={cap}
              series={series}
              projectionLow={data?.projection_low}
              projectionHigh={data?.projection_high}
            />
          )}

          <div className="mt-4 flex items-center gap-6">
            <Figure label="Spend" value={fmtEur(selected.actual)} />
            <Figure label="Forecast" value={fmtEur(selected.forecast)} />
            <Figure label="Cap" value={fmtEur(selected.budget)} />
            <Figure label="Overage" value={fmtEur(selected.projected_overage)} danger={(selected.projected_overage || 0) > 0} />
          </div>

          <Section title="Guardrail">
            {policy ? (
              <ol className="flex flex-col gap-2">
                {(Array.isArray(policy.steps) ? policy.steps : []).map((s, i) => {
                  const gate = s.action === "approval_gate";
                  return (
                    <li key={i} className={`flex items-start gap-2.5 text-xs ${gate ? "text-warning" : "text-text-2"}`}>
                      <span className={`mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${gate ? "bg-warning" : "bg-text-3"}`} />
                      {`${stepPctLabel(policy, s.trigger_level)}${TRIGGER_LABEL[s.trigger_level] || s.trigger_level} → ${gate ? "escalate to finance · require approval" : "notify owner"}`}
                    </li>
                  );
                })}
                {(!policy.steps || policy.steps.length === 0) && (
                  <li className="text-xs text-text-3">No escalation steps configured.</li>
                )}
              </ol>
            ) : (
              <div>
                <div className="text-xs font-medium text-warning">No policy attached — breaches aren&apos;t gated</div>
                <button
                  type="button"
                  onClick={() => handle("attach_policy")}
                  className="mt-2.5 rounded-ctrl bg-primary px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-primary-hover"
                >
                  Attach policy
                </button>
              </div>
            )}
          </Section>

          <Section title="Approval">
            {approvalRequestId ? (
              <>
                <div className="text-xs font-medium text-warning">Awaiting approval</div>
                <button
                  type="button"
                  onClick={() => handle("review")}
                  className="mt-2.5 rounded-ctrl bg-primary px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-primary-hover"
                >
                  Review
                </button>
              </>
            ) : hasGate ? (
              <div className="text-xs text-text-2">
                An approval is requested automatically when this budget crosses its hard cap.
              </div>
            ) : (
              <div className="text-xs text-text-3">No approval gate configured for this budget.</div>
            )}
          </Section>
        </>
      )}
    </RightSheet>
  );
}

EnforcementBudgetDrawer.propTypes = {
  selected: PropTypes.shape({
    budget_id: PropTypes.number,
    name: PropTypes.string,
    actual: PropTypes.number,
    forecast: PropTypes.number,
    budget: PropTypes.number,
    projected_overage: PropTypes.number,
    status: PropTypes.string,
    policy_id: PropTypes.number,
  }),
  policies: PropTypes.array,
  period: PropTypes.string,
  approvalRequestId: PropTypes.number,
  onClose: PropTypes.func,
  onAction: PropTypes.func,
};
